import React, { useState, useContext } from "react";
import { useTranslation } from "react-i18next";
import { UserContext } from "../../UserContext";
import axios from "axios";
import "./styles.css";
import { FaEye, FaEyeSlash, FaSave } from "react-icons/fa";

const Settings = () => {
  const { t } = useTranslation();
  const { userData, token } = useContext(UserContext);

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");

    // 1. Client-Side Validation (Your existing code is perfect)
    if (!newPassword || !confirmPassword) {
      setError(t("Please fill out both password fields."));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t("The new passwords do not match."));
      return;
    }
    if (newPassword.length < 6) {
      setError(t("Password must be at least 6 characters long."));
      return;
    }

    setIsLoading(true);

    // 2. Define the Request Body as per the API documentation
    // We set isEmail to false because we are authenticating with the token, not an email identifier.
    const requestBody = {
      newPassword: newPassword,
      isEmail: false,
      firebaseId: userData.User.firebaseId,
    };

    try {
      // 3. Make the API call to the correct endpoint with authentication
      // The endpoint is POST /api/user/resetPassword
      const response = await axios.post(
        "/api/user/resetPassword", // NOTE: Ensure your axios instance is configured with the base URL e.g., http://34.226.212.12/egrocare-api/egrocare/ [cite: 4]
        requestBody,
        {
          headers: {
            // The API requires the Firebase ID Token for authentication
            Authorization: token,
          },
        }
      );

      // 4. Handle the success response based on the API's structure [cite: 11]
      setSuccessMessage(
        response.data.message || t("Password changed successfully!")
      );
      setNewPassword(""); // Clear the input fields
      setConfirmPassword("");
    } catch (err) {
      // 5. Handle the error response based on the API's structure [cite: 19]
      const errorMessage =
        err.response?.data?.error || // Use the specific error message from the backend [cite: 23]
        t("Failed to update password. Please try again.");
      setError(errorMessage);
    } finally {
      // 6. Ensure the loading state is always reset
      setIsLoading(false);
    }
  };

  return (
    <div className="settings-container">
      <h1>{t("Settings")}</h1>
      <div className="settings-card">
        <h2>{t("Reset Password")}</h2>
        <p className="settings-description">
          {t("Enter a new password for your account below.")}
        </p>
        <form onSubmit={handleSubmit} className="settings-form">
          <div className="form-group">
            <label htmlFor="newPassword">{t("New Password")}</label>
            <div className="password-input-wrapper">
              <input
                id="newPassword"
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder={t("Enter your new password")}
              />
              <span
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="password-toggle"
              >
                {showNewPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="confirmPassword">{t("Confirm New Password")}</label>
            <div className="password-input-wrapper">
              <input
                id="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder={t("Confirm your new password")}
              />
              <span
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="password-toggle"
              >
                {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>
          </div>

          {error && <div className="form-message error-message">{error}</div>}
          {successMessage && (
            <div className="form-message settings-success-message">
              {successMessage}
            </div>
          )}

          <div className="form-actions">
            <button type="submit" className="save-button" disabled={isLoading}>
              {isLoading ? (
                t("Saving...")
              ) : (
                <>
                  <FaSave /> {t("Save Changes")}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Settings;
